import gameStyles from "./scss/game.module.scss";
import scrollStyle from "./scss/scrollbar.module.scss";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  closestCorners,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { easeInOut, motion, stagger, useAnimate } from "framer-motion";

import {
  setAnimating,
  finishGame,
  goToQuestion,
} from "@educaplay/store/slices/gameSlice";
import {
  Fill,
  // NAME_ANIMATION_POINTS,
  // useGameAnimations,
  useGameSounds,
  useMediaQuery,
  useTranslate,
  VisualFeedback,
} from "@educaplay/core";

import SortableList from "./components/SortableList";
import DraggableBox from "./components/DraggableBox";
import OverlayWrapper from "./components/OverlayWrapper";
import AnimatedCard from "./components/AnimatedCard/AnimatedCard.jsx";
import ContentSelector from "./components/ContentSelector";
import CheckButton from "./components/CheckButton";
import Bullet from "./components/Bullet";

import permute from "./utils/permute";

import blurAll from "./utils/blurAll";

import wait from "./utils/wait.js";
import listAnim from "./utils/listAnim";
import playCardSounds from "./utils/playCardSounds";

import {
  ANIMATION_DURATIONS,
  CARD_ANIMS,
  CARD_CORRECTIONS,
  LINE_UP_SOUND_NAMES,
} from "./utils/consts";

import clsx from "clsx";
import useHorizontalScroll from "./utils/useHorizontalScroll.js";
import { SCREENS } from "@educaplay/store/constants.js";

// #region Game Component
const Game = forwardRef(function Game({ onAnswer }, ref) {
  const questions = useSelector((state) => state.game.questions);
  const currentQuestionIndex = useSelector((state) => state.game.currentIndex);
  const currentQuestion = questions[currentQuestionIndex];
  const penalties = useSelector((state) => state.game.liveActived);
  const hasFinished =
    useSelector((state) => state.game.view) === SCREENS.SCORE_SCREEN;

  const isAnimating = useSelector((state) => state.game.isAnimating);
  const [isChecking, setChecking] = useState(false);

  const [order, setOrder] = useState(currentQuestion.items);
  const dragBoxRefs = useRef([]);

  const dispatch = useDispatch();

  const [sortableListRef, animateList] = useAnimate();
  const [gameAreaRef, animateArea] = useAnimate();
  const visualFeedbackRef = useRef();

  const gameSounds = useGameSounds();

  const sensors = useSensors(
    // ORDER IS IMPORTANT, DO NOT OVERWRITE SENSOR ACTIVATOR EVENTS
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 15,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: ANIMATION_DURATIONS.holdTime2Drag_ms,
        tolerance: 10,
      },
    })
  );

  useEffect(() => {
    setOrder(currentQuestion.items);
    dragBoxRefs.current = [];

    const initAnimation = async () => {
      dispatch(setAnimating(true));
      playCardSounds({
        player: gameSounds,
        sound: LINE_UP_SOUND_NAMES.cardLoad,
        nCards: currentQuestion.items.length,
        delay: ANIMATION_DURATIONS.cardSpawn_ms,
      });
      await animateArea(gameAreaRef.current, { scale: 1 }, { duration: 0.5 });
      dispatch(setAnimating(false));
    };

    initAnimation();
  }, [animateArea, currentQuestion.items, dispatch, gameAreaRef, gameSounds]);

  const [activeItem, setActiveItem] = useState(null);

  const [isGrabbed, setGrabbed] = useState(false); // Active status (non-library ops)
  const [cardsReady, setCardsReady] = useState(true);
  const [bulletPresent, setBulletPresent] = useState(false);

  useImperativeHandle(ref, () => ({
    animateEntry: async () => {
      animateArea(
        gameAreaRef.current,
        { scaleX: 1, scaleY: 1 },
        { duration: 0.5 }
      );
    },
  }));

  useHorizontalScroll(sortableListRef, gameAreaRef);

  const horizontalLayout = useMediaQuery("(orientation: landscape)");

  // #region interaction no drag

  function clearClickSelection() {
    setOrder(
      order.map((it) => {
        return { ...it, active: false };
      })
    );
  }

  function handleCardtap(item) {
    if (isAnimating || isGrabbed || item.locked || hasFinished) {
      return;
    }

    const swapStatus = activeItem != null;

    if (swapStatus) {
      // Drop
      const from = order.findIndex((it) => it.id === activeItem.id);
      const to = order.findIndex((it) => it.id === item.id);

      if (from === to) {
        gameSounds.play(LINE_UP_SOUND_NAMES.clickCard);
      } else {
        gameSounds.play(LINE_UP_SOUND_NAMES.moveCard);
      }

      const newArr = permute({
        array: order,
        from,
        to,
        lockedArr: order.map((it) => it.locked),
      });

      setOrder(newArr);

      setTimeout(() => {
        setOrder(
          newArr.map((it) => ({
            ...it,
            active: false,
          }))
        );
        setActiveItem(null);
        blurAll();
      }, ANIMATION_DURATIONS.dndSwap_ms);
    } else {
      setActiveItem(item);
      gameSounds.play(LINE_UP_SOUND_NAMES.clickCard);

      setOrder(order.map((it) => ({ ...it, active: it.id === item.id })));
    }
  }

  async function handleCheck() {
    if (isAnimating || isChecking || hasFinished) {
      return;
    }

    dispatch(setAnimating(true));
    setChecking(true);

    gameSounds.play(LINE_UP_SOUND_NAMES.checkAnswer);

    clearClickSelection();
    setActiveItem(null);
    setGrabbed(false);

    const orderCopy = order.map((it, idx) => ({
      ...it,
      correction:
        it.id === idx + 1
          ? it.locked
            ? CARD_CORRECTIONS.locked
            : CARD_CORRECTIONS.ok
          : CARD_CORRECTIONS.wrong,
    }));

    await setOrder(orderCopy);

    const nWrong = orderCopy.filter(
      (x) => x.correction === CARD_CORRECTIONS.wrong
    ).length;
    const nRight = orderCopy.filter(
      (x) => x.correction === CARD_CORRECTIONS.ok
    ).length;
    const isCorrect = nWrong === 0;

    // let points = 0;
    // Bounce correct cards
    if (nRight > 0) {
      gameSounds.play(LINE_UP_SOUND_NAMES.checkRight);

      playCardSounds({
        player: gameSounds,
        sound: LINE_UP_SOUND_NAMES.cardCorrect,
        nCards: nRight,
        delay: ANIMATION_DURATIONS.correctCardStagger_ms,
      });

      let counter = 0;
      dragBoxRefs.current.forEach(async (el) => {
        if (!el) return;

        if (el.needsScroll()) {
          await wait(ANIMATION_DURATIONS.correctCardStagger_ms * counter++);
          el.scrollHere(counter != 1 ? "smooth" : "instant");
        }
      });

      await wait(ANIMATION_DURATIONS.correctCardStagger_ms / 2);

      await listAnim({
        animateFunc: animateList,
        cardId: CARD_CORRECTIONS.ok,
        animation: { ...CARD_ANIMS.bounce.anim },
        transition: {
          ...CARD_ANIMS.bounce.transition,
          delay: stagger(ANIMATION_DURATIONS.correctCardStagger),
        },
      });
    } else if (!penalties) {
      setBulletPresent(true);
      setTimeout(() => {
        setBulletPresent(false);
      }, 3000 + nWrong * ANIMATION_DURATIONS.wrongCardStagger_ms);
    }

    onAnswer(
      orderCopy.map((it) => ({
        id: it.id,
        data: it.data,
        correction: it.correction,
      })),
      {
        delayLiveAnimation:
          nWrong * ANIMATION_DURATIONS.wrongCardStagger_ms +
          ANIMATION_DURATIONS.life2PointDelay_ms,
        showFeedback: false,
        playErrorSound: penalties,
        nextQuestionIndex: currentQuestionIndex,
      }
    );

    if (nRight > 0) await wait(ANIMATION_DURATIONS.life2PointDelay_ms);

    // Shake wrong cards
    if (penalties && !isCorrect) {
      listAnim({
        animateFunc: animateList,
        cardId: CARD_CORRECTIONS.wrong,
        animation: CARD_ANIMS.shake.anim,
        transition: {
          ...CARD_ANIMS.shake.transition,
          delay: stagger(ANIMATION_DURATIONS.wrongCardStagger),
        },
      });

      await playCardSounds({
        player: gameSounds,
        sound: LINE_UP_SOUND_NAMES.cardFail,
        nCards: nWrong,
        delay: ANIMATION_DURATIONS.wrongCardStagger_ms,
      });

      await wait(ANIMATION_DURATIONS.wrongCardStagger_ms);

      // Red back to normal
      await listAnim({
        animateFunc: animateList,
        cardId: CARD_CORRECTIONS.wrong,
        animation: CARD_ANIMS.default.anim,
        transition: CARD_ANIMS.default.transition,
      });
    } else if (!isCorrect) {
      gameSounds.play(LINE_UP_SOUND_NAMES.answerFailSmooth);

      const anim = horizontalLayout
        ? CARD_ANIMS.shakeY_smooth
        : CARD_ANIMS.shakeX_smooth;

      await listAnim({
        animateFunc: animateList,
        cardId: CARD_CORRECTIONS.wrong,
        animation: anim.anim,
        transition: {
          ...anim.transition,
          delay: stagger(ANIMATION_DURATIONS.wrongCardStagger),
        },
      });

      await wait(ANIMATION_DURATIONS.wrongCardStagger_ms);

      // Red back to normal
      await listAnim({
        animateFunc: animateList,
        cardId: CARD_CORRECTIONS.wrong,
        animation: CARD_ANIMS.default.anim,
        transition: CARD_ANIMS.default.transition,
      });
    }

    // If all correct: swap index to next question
    // Else: lock correct items
    if (nWrong === 0) {
      const hasNext = currentQuestionIndex + 1 < questions.length;

      // Reduce to oblivion
      if (hasNext) {
        setCardsReady(false);
        await animateArea(
          gameAreaRef.current,
          { scale: 0 },
          { duration: 0.3, ease: easeInOut }
        );
      }

      // Update state
      const action = hasNext
        ? goToQuestion(currentQuestionIndex + 1)
        : finishGame();

      dispatch(action);

      // Respawn if next
      if (hasNext) {
        setTimeout(() => setCardsReady(true), 300);
        animateArea(
          gameAreaRef.current,
          { scale: 1 },
          { duration: 0.3, ease: easeInOut }
        );
      }
    } else {
      // Cards left to move
      await setOrder(
        orderCopy.map((it, idx) => ({
          ...it,
          locked: it.id === idx + 1,
        }))
      );
    }

    setChecking(() => false);
    dispatch(setAnimating(false));
  }

  // #region dnd-kit

  const rectSortBlockStrat = ({ rects, activeIndex, overIndex, index }) => {
    const newRects = permute({
      array: rects,
      from: overIndex,
      to: activeIndex,
      lockedArr: order.map((it) => it.locked),
    });

    const oldRect = rects[index];
    const newRect = newRects[index];

    if (!newRect || !oldRect) {
      return null;
    }

    return {
      x: newRect.left - oldRect.left,
      y: newRect.top - oldRect.top,
      // Scale should be omitted, all cards are equal size
      scaleX: oldRect.width / newRect.width,
      scaleY: oldRect.height / newRect.height,
    };
  };

  async function handleDragStart(evt) {
    clearClickSelection();

    const active = evt.active;
    const activeCard = order.find((it) => it.id === active.id);

    if (activeCard) {
      setActiveItem(activeCard);
      setGrabbed(true);
    }
  }

  async function handleDragCancel(evt) {
    clearClickSelection();
    setActiveItem(null);
    setGrabbed(false);

    if (evt.activatorEvent.type !== "keydown") {
      blurAll();
    }
  }

  async function handleDragEnd(evt) {
    // Unfocus on pointer evt
    clearClickSelection();
    if (evt.activatorEvent.type !== "keydown") {
      blurAll();
    }

    const active = evt.active;
    const over = evt.over;

    if (!active || !over) return;

    const activeIndex = order.findIndex((it) => it.id === active.id);
    const overIndex = order.findIndex((it) => it.id === over.id);

    setGrabbed(false);

    setOrder(
      permute({
        array: order,
        from: activeIndex,
        to: overIndex,
        lockedArr: order.map((it) => it.locked),
      })
    );

    setActiveItem(null);

    wait(ANIMATION_DURATIONS.dragOverlayDrop_ms);
    gameSounds.play(LINE_UP_SOUND_NAMES.placeCard);
  }

  const t = useTranslate();

  // #region JSX
  return (
    <>
      <Fill name={"core-bank-right"}>
        {questions.length !== 1 &&
          `${t("specific.screen")}: ` +
            `${currentQuestionIndex + 1}/${questions.length}`}
      </Fill>
      <Fill name={"core-bank-left"}>
        {t("specific.cards")}
        {": "}
        {
          order.filter(
            (it) =>
              it.correction &&
              it.correction != CARD_CORRECTIONS.uncheked &&
              it.correction != CARD_CORRECTIONS.wrong
          ).length
        }
        /{order.length}
      </Fill>
      <VisualFeedback ref={visualFeedbackRef} />

      <motion.div
        className={gameStyles.gameArea}
        ref={gameAreaRef}
        animate={{ scale: 1 }}
        initial={{ scale: 0 }}
      >
        <Bullet open={bulletPresent} text={t("specific.incorrectOrder")} />
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
          onDragOver={() => {
            gameSounds.play(LINE_UP_SOUND_NAMES.moveCard);
          }}
        >
          <div className={clsx(gameStyles.interior, scrollStyle.scrollbar)}>
            <div className={gameStyles.arrow} />
            <SortableList
              strat={rectSortBlockStrat}
              itemIds={order.map((it) => it.id)}
              ref={sortableListRef}
            >
              {order.map((it, idx) => (
                <DraggableBox
                  key={currentQuestionIndex + "-" + it.id}
                  item={it}
                  onClick={handleCardtap}
                  checkLock={isAnimating || isChecking || hasFinished}
                  idx={idx}
                  ref={(el) => (dragBoxRefs.current[idx] = el)}
                  onStage={cardsReady}
                >
                  <ContentSelector
                    locked={it.locked}
                    data={{ ...it.data, active: it.active }}
                  />
                </DraggableBox>
              ))}
            </SortableList>
            <CheckButton onClick={handleCheck}></CheckButton>
          </div>
          <OverlayWrapper rotateDeg={7}>
            {activeItem && (
              <AnimatedCard atOverlay>
                <ContentSelector data={activeItem.data} />
              </AnimatedCard>
            )}
          </OverlayWrapper>
        </DndContext>
      </motion.div>
    </>
  );
});

export default Game;
